import { Menu, X } from 'lucide-react'
import { useState } from 'react'
import { User } from '../types'
import { useAuthStore } from '../stores'
import { initPayment } from '../utils/payments'
import getSubscription from '../utils/subscriptions'
import { useEffect } from 'react'

interface NavbarProps {
  currentView: string
  onNavigate: (view: string) => void
  onLoginClick: () => void
  onLogoutClick: () => void
  user: User | null
}

export function Navbar({ currentView, onNavigate, onLoginClick, onLogoutClick, user }: NavbarProps) {
  const [isOpen, setIsOpen] = useState(false)
  const { user: authUser } = useAuthStore()
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null)
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false)

  useEffect(() => {
    let mounted = true
    async function check() {
      if (!authUser) {
        if (mounted) setHasActiveSubscription(false)
        return
      }
      try {
        const sub = await getSubscription()
        const active = !!(sub && (sub.status === 'active' || sub.active))
        if (mounted) setHasActiveSubscription(active)
      } catch (e) {
        if (mounted) setHasActiveSubscription(false)
      }
    }
    check()
    return () => { mounted = false }
  }, [authUser])

  useEffect(() => {
    const syncDeferredPrompt = () => {
      const prompt = (window as any).__deferredPwaPrompt
      if (prompt) {
        prompt.preventDefault?.()
        setDeferredPrompt(prompt)
      }
    }

    syncDeferredPrompt()
    window.addEventListener('beforeinstallprompt', syncDeferredPrompt)
    return () => window.removeEventListener('beforeinstallprompt', syncDeferredPrompt)
  }, [])

  const navLinks = [
    { id: 'home', label: 'Home' },
    { id: 'explore', label: 'Explore' },
    { id: 'top-talents', label: 'Top Talents' },
    { id: 'blog', label: 'Blog' },
    { id: 'about', label: 'About Us' },
    { id: 'how-it-works', label: 'How it Works' },
    { id: 'pricing', label: 'Pricing' },
    { id: 'support', label: 'Support' },
  ]

  const handleNavigate = (id: string) => {
    onNavigate(id)
    setIsOpen(false)
  }

  return (
    <nav className="sticky top-0 z-50 bg-[#050910]/80 backdrop-blur-md border-b border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-14 sm:h-16">
          <div className="flex items-center gap-2.5 cursor-pointer group" onClick={() => handleNavigate('home')}>
            <img src="/scoutang-logo.svg" alt="Scouta Logo" className="w-8 h-8 sm:w-9 sm:h-9 transition-transform group-hover:scale-110" />
            <span className="text-lg sm:text-xl font-black text-[#7cff01] tracking-tight">Scouta</span>
          </div>

          {/* Desktop Links */}
          <div className="hidden md:block">
            <div className="flex items-center gap-1">
              {navLinks.map((link) => (
                <button
                  key={link.id}
                  onClick={() => handleNavigate(link.id)}
                  className={`px-3 py-1.5 rounded-xl text-sm font-medium transition-all ${
                    currentView === link.id
                      ? 'bg-[#7cff01]/10 text-[#7cff01]'
                      : 'text-gray-400 hover:text-white hover:bg-white/5'
                  }`}
                >
                  {link.label}
                </button>
              ))}
              {user ? (
                <div className="ml-2 flex items-center gap-2">
                {/* Upgrade CTA for non-subscribed users */}
                {!hasActiveSubscription && (
                  <button
                    onClick={async () => {
                      if (!authUser) return onLoginClick()
                      try {
                        const { authorization_url } = await initPayment(authUser.role === 'player' ? 'player_pro' : authUser.role === 'scout' ? 'scout_pro' : 'club_pro')
                        window.location.href = authorization_url
                      } catch (err: any) {
                        alert(err.message || 'Failed to start payment')
                      }
                    }}
                    className="hidden sm:inline px-3 py-1.5 rounded-xl bg-yellow-400 text-black font-bold"
                  >
                    Upgrade
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => handleNavigate('dashboard')}
                  className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-2 py-1.5 text-left transition hover:bg-white/10"
                  title="Open dashboard"
                >
                  <div className="h-7 w-7 overflow-hidden rounded-lg border border-white/10 bg-black/30">
                    {user.avatarUrl ? (
                      <img src={user.avatarUrl} alt={`${user.name} avatar`} className="h-full w-full object-cover" />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center bg-[#0f1d22] text-[10px] font-black uppercase text-[#7cff01]">
                        {user.initials}
                      </div>
                    )}
                  </div>
                  <div className="hidden lg:block pr-1">
                    <p className="text-xs font-black text-white leading-none">{user.name}</p>
                    <p className="mt-1 text-[10px] font-bold uppercase tracking-widest text-gray-400">
                      {user.role === 'scout' ? 'Scout' : user.role === 'player' ? 'Player' : user.role === 'sub_admin' ? 'Sub-Admin' : user.role}
                    </p>
                  </div>
                </button>
                <button
                  onClick={onLogoutClick}
                  className="px-4 py-1.5 text-xs font-black uppercase tracking-widest text-red-400 border border-red-900/30 bg-red-900/10 hover:bg-red-900/20 rounded-xl transition-all"
                >
                  Logout
                </button>
                  {deferredPrompt && (
                    <button
                      onClick={async () => {
                        try {
                          deferredPrompt.prompt()
                          const choice = await deferredPrompt.userChoice
                          if (choice.outcome === 'accepted') setDeferredPrompt(null)
                        } catch (e) {
                          console.warn('PWA install prompt failed', e)
                        }
                      }}
                      className="ml-2 px-3 py-1.5 rounded-xl border border-white/10 bg-white/5 text-sm"
                    >
                      Install App
                    </button>
                  )}
                </div>
              ) : (
                <button
                  onClick={onLoginClick}
                  className="ml-2 btn-primary py-2 px-5 text-sm"
                >
                  Sign In
                </button>
              )}
            </div>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 rounded-xl border border-white/10 text-gray-400 hover:text-white hover:bg-white/5 transition-all"
          >
            {isOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu — frosted glass dropdown */}
      {isOpen && (
        <div className="md:hidden bg-[#050910]/95 backdrop-blur-xl border-b border-white/5 animate-in slide-in-from-top duration-200">
          <div className="px-4 py-4 space-y-1">
            {navLinks.map((link) => (
              <button
                key={link.id}
                onClick={() => handleNavigate(link.id)}
                className={`flex w-full items-center px-4 py-3 rounded-2xl text-sm font-bold transition-all ${
                  currentView === link.id
                    ? 'bg-[#7cff01]/10 text-[#7cff01]'
                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                }`}
              >
                {link.label}
              </button>
            ))}
            <div className="pt-2 border-t border-white/5">
              <div className="mt-2 rounded-2xl border border-[#7cff01]/20 bg-[#7cff01]/5 p-3">
                <p className="text-[10px] font-black uppercase tracking-[0.24em] text-[#7cff01]">Download Scouta</p>
                <div className="mt-2 space-y-2 text-xs text-gray-300">
                  <div>
                    <p className="font-bold text-white">iPhone</p>
                    <p>Open Safari → Tap Share → Add to Home Screen.</p>
                  </div>
                  <div>
                    <p className="font-bold text-white">Android</p>
                    <p>Open Chrome → Tap Menu → Install app / Add to Home Screen.</p>
                  </div>
                </div>
              </div>
              {user ? (
                <>
                  <button
                    type="button"
                    onClick={() => handleNavigate('dashboard')}
                    className="w-full mt-2 flex items-center gap-3 px-4 py-3 text-left rounded-2xl border border-white/10 bg-white/5 transition-all hover:bg-white/10"
                  >
                    <div className="h-10 w-10 overflow-hidden rounded-xl border border-white/10 bg-black/30">
                      {user.avatarUrl ? (
                        <img src={user.avatarUrl} alt={`${user.name} avatar`} className="h-full w-full object-cover" />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center bg-[#0f1d22] text-[11px] font-black uppercase text-[#7cff01]">
                          {user.initials}
                        </div>
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-black text-white">{user.name}</p>
                      <p className="mt-1 text-[10px] font-bold uppercase tracking-widest text-gray-400">
                        {user.role === 'scout' ? 'Scout' : user.role === 'player' ? 'Player' : user.role === 'sub_admin' ? 'Sub-Admin' : user.role}
                      </p>
                    </div>
                  </button>
                  <button
                    onClick={onLogoutClick}
                    className="w-full mt-2 px-4 py-3 text-xs font-black uppercase tracking-widest text-red-400 border border-red-900/30 bg-red-900/10 hover:bg-red-900/20 rounded-2xl transition-all"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <button
                  onClick={onLoginClick}
                  className="w-full mt-2 btn-primary py-3 px-4 text-center"
                >
                  Sign In
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  )
}
